// src/stations/gallery/galleryStation.ts — Victorian Naturalist Studio & 2D Specimen Gallery
// Integrates 2D Engraved Plates and Biological Specimen Field Stamps (FR-ART-001, FR-ART-002)

import { renderCornerFiligree, renderCompassWatermark } from '../../ui/components/ornateHelpers';
import { ExplorerPointsManager } from '../../storage/explorerPoints';
import { StreakManager } from '../../storage/streakManager';

export type SpecimenPlateType = 'helmet' | 'compass' | 'lobster' | 'shrimp';

export class GalleryStation {
  private container: HTMLElement;
  private currentPlate: SpecimenPlateType = 'helmet';

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'gallery-station-view';
    this.container.style.cssText = `
      width: 100%;
      max-width: 1080px;
      margin: 0 auto;
      padding: 0 0 40px 0;
      touch-action: manipulation;
    `;
  }

  render(): HTMLElement {
    const totalEp = ExplorerPointsManager.getTotalEp();
    const streakStatus = StreakManager.checkStatus();

    this.container.innerHTML = `
      <!-- Studio Header Banner -->
      <section class="vellum-surface morris-card morris-card-gold" style="margin-bottom: 24px; padding: 26px 24px; position: relative;">
        ${renderCornerFiligree()}
        ${renderCompassWatermark()}
        <div style="position: relative; z-index: 2;">
          <div class="atelier-eyebrow-pill" style="margin-bottom: 8px;">
            <span>🎨</span>
            <span>NATURALIST STUDIO &amp; ENGRAVED ARCHIVE</span>
          </div>
          <h2 style="font-family: var(--font-heading); font-size: 2rem; color: var(--morris-indigo); margin: 0 0 8px 0;">
            Field Specimen Gallery &amp; Collectibles
          </h2>
          <p style="font-size: 0.95rem; line-height: 1.5; color: #333; max-width: 720px; margin: 0;">
            Inspect authentic Victorian engraved naturalist plates and view historical field stamps unlocked through Tudor's daily practice and Explorer Points (${totalEp} EP, ${streakStatus.currentStreak}-day voyage).
          </p>
        </div>
      </section>

      <!-- 2D Victorian Naturalist Showcase Plate Section -->
      <section class="vellum-surface" style="
        margin-bottom: 28px;
        padding: 26px 24px;
        border-radius: 16px;
        border: 2px solid var(--gilded-brass);
        box-shadow: 0 10px 30px rgba(10, 25, 47, 0.2);
        position: relative;
      ">
        ${renderCornerFiligree()}
        <div style="position: relative; z-index: 2;">
          <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 20px;">
            <div>
              <h3 style="font-family: var(--font-heading); font-size: 1.4rem; color: var(--morris-indigo); margin: 0;">
                Tactile Victorian Naturalist Showcase
              </h3>
              <div style="font-size: 0.85rem; color: #555; margin-top: 4px;">
                Select an authentic 2D engraved plate from the Challenger Expedition archive.
              </div>
            </div>
            <!-- Collectible Selector Buttons -->
            <div style="display: flex; gap: 8px; flex-wrap: wrap;">
              <button class="touch-target btn-specimen-select btn-brass" data-plate="helmet" style="padding: 8px 14px; font-size: 0.85rem; min-height: 44px; border-radius: 999px;">
                🤿 Diving Apparatus
              </button>
              <button class="touch-target btn-specimen-select btn-vellum" data-plate="compass" style="padding: 8px 14px; font-size: 0.85rem; min-height: 44px; border-radius: 999px;">
                🧭 Mariner Compass
              </button>
              <button class="touch-target btn-specimen-select btn-vellum" data-plate="lobster" style="padding: 8px 14px; font-size: 0.85rem; min-height: 44px; border-radius: 999px;">
                🦞 Homarus Plate
              </button>
              <button class="touch-target btn-specimen-select btn-vellum" data-plate="shrimp" style="padding: 8px 14px; font-size: 0.85rem; min-height: 44px; border-radius: 999px;">
                🦐 Squilla Plate
              </button>
            </div>
          </div>

          <!-- Showcase Display Stage -->
          <div class="atelier-hero-plate" style="
            min-height: 260px;
            background: radial-gradient(circle at 45% 40%, rgba(244, 235, 205, 0.6) 0%, rgba(239, 226, 194, 0.4) 100%);
            border-radius: 14px;
            border: 2px dashed var(--gilded-brass);
            padding: 24px;
          ">
            <img 
              id="gallery-showcase-img"
              src="/assets/atelier/brass-diving-helmet.png" 
              alt="H.M.S. Challenger Diving Apparatus" 
              class="atelier-engraving-blend"
              style="max-width: 200px; height: auto;"
            />
          </div>

          <!-- Active Plate Information Box -->
          <div id="gallery-specimen-info" style="margin-top: 16px; padding: 14px 18px; background: rgba(26, 54, 93, 0.05); border-radius: 10px; border: 1px solid rgba(229, 169, 60, 0.3);">
            <div id="specimen-title" style="font-weight: 800; font-family: var(--font-heading); color: var(--morris-indigo); font-size: 1.1rem;">
              H.M.S. Challenger Deep-Sea Diving Apparatus (Siebe Gorman &amp; Co.)
            </div>
            <div id="specimen-desc" style="font-size: 0.88rem; color: #444; margin-top: 4px; line-height: 1.45;">
              Heavy copper and brass 12-bolt deep-sea exploration helmet with twin observation ports and counter-balanced air exhaust valve, engineered for abyssal oceanographic exploration.
            </div>
          </div>
        </div>
      </section>

      <!-- Biological Specimen Field Stamps Section -->
      <section class="vellum-surface" style="
        padding: 26px 24px;
        border-radius: 16px;
        border: 2px solid var(--gilded-brass);
        box-shadow: 0 10px 30px rgba(10, 25, 47, 0.2);
        position: relative;
      ">
        ${renderCornerFiligree()}
        <div style="position: relative; z-index: 2;">
          <div style="margin-bottom: 20px;">
            <div class="atelier-eyebrow-pill" style="margin-bottom: 6px;">
              <span>🏷️</span>
              <span>NATURALIST ARCHIVE</span>
            </div>
            <h3 style="font-family: var(--font-heading); font-size: 1.4rem; color: var(--morris-indigo); margin: 0;">
              Unlocked Biological Specimen Stamps
            </h3>
            <p style="font-size: 0.9rem; color: #555; margin: 4px 0 0 0;">
              Authentic biological plates and taxonomy notes earned through Explorer Points (${totalEp} EP, ${streakStatus.currentStreak}-day voyage) and practice consistency.
            </p>
          </div>

          <!-- Stamps Grid -->
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 20px;">
            <!-- Stamp 1: Homarus Gammarus -->
            <article class="card-interactive" style="
              background: #FFFDF9;
              border: 2px solid var(--gilded-brass);
              border-radius: 12px;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              box-shadow: 0 4px 12px rgba(10, 25, 47, 0.1);
            ">
              <div style="width: 140px; height: 140px; margin-bottom: 14px; display: flex; align-items: center; justify-content: center;">
                <img 
                  src="/assets/atelier/stamp-homarus-gammarus.png" 
                  alt="Homarus gammarus Stamp" 
                  class="atelier-specimen-stamp atelier-engraving-blend"
                  style="max-width: 100%; max-height: 100%; object-fit: contain;" 
                />
              </div>
              <div style="font-size: 0.75rem; text-transform: uppercase; font-weight: 800; color: var(--gilded-brass); letter-spacing: 0.08em; margin-bottom: 4px;">
                Plate I · Decapoda
              </div>
              <h4 style="font-family: var(--font-heading); font-size: 1.15rem; color: var(--morris-indigo); margin: 0 0 6px 0;">
                Homarus gammarus
              </h4>
              <div style="font-style: italic; font-size: 0.85rem; color: #666; margin-bottom: 8px;">
                European Lobster · Linnaeus, 1758
              </div>
              <p style="font-size: 0.85rem; color: #444; line-height: 1.4; margin: 0;">
                Possesses asymmetrical chelae: a heavy crusher claw for molluscs and a serrated cutter claw for rapid defence along rocky benthic shelves.
              </p>
              <div style="margin-top: 14px; font-size: 0.75rem; font-weight: 700; color: #166534; background: #DCFCE7; padding: 4px 10px; border-radius: 999px;">
                ✓ Field Guide Certified
              </div>
            </article>

            <!-- Stamp 2: Squilla Mantis -->
            <article class="card-interactive" style="
              background: #FFFDF9;
              border: 2px solid var(--gilded-brass);
              border-radius: 12px;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              text-align: center;
              box-shadow: 0 4px 12px rgba(10, 25, 47, 0.1);
            ">
              <div style="width: 140px; height: 140px; margin-bottom: 14px; display: flex; align-items: center; justify-content: center;">
                <img 
                  src="/assets/atelier/stamp-squilla-mantis.png" 
                  alt="Squilla mantis Stamp" 
                  class="atelier-specimen-stamp atelier-engraving-blend"
                  style="max-width: 100%; max-height: 100%; object-fit: contain;" 
                />
              </div>
              <div style="font-size: 0.75rem; text-transform: uppercase; font-weight: 800; color: var(--gilded-brass); letter-spacing: 0.08em; margin-bottom: 4px;">
                Plate II · Stomatopoda
              </div>
              <h4 style="font-family: var(--font-heading); font-size: 1.15rem; color: var(--morris-indigo); margin: 0 0 6px 0;">
                Squilla mantis
              </h4>
              <div style="font-style: italic; font-size: 0.85rem; color: #666; margin-bottom: 8px;">
                Spottail Mantis Shrimp · Fabricius, 1787
              </div>
              <p style="font-size: 0.85rem; color: #444; line-height: 1.4; margin: 0;">
                Burrows in Mediterranean muddy sands. Renowned for hyper-spectral trinocular vision and raptorial dactyls that strike with bullet-velocity cavitations.
              </p>
              <div style="margin-top: 14px; font-size: 0.75rem; font-weight: 700; color: #166534; background: #DCFCE7; padding: 4px 10px; border-radius: 999px;">
                ✓ Field Guide Certified
              </div>
            </article>
          </div>
        </div>
      </section>
    `;

    this.attachEvents();
    return this.container;
  }

  private attachEvents(): void {
    const buttons = this.container.querySelectorAll<HTMLButtonElement>('.btn-specimen-select');
    const imgEl = this.container.querySelector<HTMLImageElement>('#gallery-showcase-img');
    const infoTitle = this.container.querySelector<HTMLElement>('#specimen-title');
    const infoDesc = this.container.querySelector<HTMLElement>('#specimen-desc');

    const plates: Record<SpecimenPlateType, { img: string; title: string; desc: string }> = {
      helmet: {
        img: '/assets/atelier/brass-diving-helmet.png',
        title: 'H.M.S. Challenger Deep-Sea Diving Apparatus (Siebe Gorman & Co.)',
        desc: 'Heavy copper and brass 12-bolt deep-sea exploration helmet with twin observation ports and counter-balanced air exhaust valve, engineered for abyssal oceanographic exploration.',
      },
      compass: {
        img: '/assets/atelier/antique-compass-rose.png',
        title: 'Victorian 32-Point Nautical Mariner Compass Rose',
        desc: 'Hand-engraved brass compass rose featuring fleur-de-lys north pointer and rhumb bearings, used by expedition navigators charting Atlantic depths.',
      },
      lobster: {
        img: '/assets/atelier/stamp-homarus-gammarus.png',
        title: 'Homarus gammarus (European Lobster) Naturalist Plate',
        desc: 'Authentic 18th-century Linnaean taxonomic engraving illustrating the crusher claw, walking pereiopods, and sensory antennae of the benthic rockpool lobster.',
      },
      shrimp: {
        img: '/assets/atelier/stamp-squilla-mantis.png',
        title: 'Squilla mantis (Spottail Mantis Shrimp) Naturalist Plate',
        desc: 'Delicate anatomical woodcut detailing the raptorial striking claws and abdominal somites of the Mediterranean stomatopod hunter.',
      },
    };

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const plate = btn.getAttribute('data-plate') as SpecimenPlateType;
        if (!plate || plate === this.currentPlate) return;

        this.currentPlate = plate;
        const data = plates[plate];
        if (!data) return;

        if (imgEl) {
          imgEl.src = data.img;
          imgEl.alt = data.title;
        }

        // Update button active states
        buttons.forEach((b) => {
          b.classList.remove('btn-brass');
          b.classList.add('btn-vellum');
        });
        btn.classList.remove('btn-vellum');
        btn.classList.add('btn-brass');

        // Update description
        if (infoTitle && infoDesc) {
          infoTitle.textContent = data.title;
          infoDesc.textContent = data.desc;
        }
      });
    });
  }

  destroy(): void {
    // No background animation loops to dispose
  }
}
